import { Apps, Info, MoreHoriz } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemThumbnail } from '~/components/ItemThumbnail';
import { ActionButton, ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { FolderIcon } from '~/components/folder-breadcrumbs/styles';
import { useLink } from '~/hooks/link';
import { formatDate } from '~/lib/datetime';
import { mediaExtension, mediaSize } from '~/lib/media';
import { formatBytes } from '~/lib/units';
import {
  ContentLocation,
  ContentMenu,
  type ContentCardProps,
  type ContentFolderCardProps,
} from '../components';

type ContentOrFolder = ContentCardProps['item'] | ContentFolderCardProps['contentFolder'];

export const useColumns = ({
  search = false,
}: {
  search: boolean;
}): GridColDef<ContentOrFolder>[] => {
  const { t } = useTranslation();
  const link = useLink();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  const getLink = useCallback(
    (item: ContentCardProps['item']) => {
      if (item.__typename === 'ContentItem' && item.design?.id) {
        return link(`/studio/${item.design.designFolderId}/design/${item.design.id}`);
      }

      if (item.__typename === 'ContentItem') {
        return link(`/content/${item.nullableContentFolderId}/apps/${item.id}/settings`);
      }

      return link(`/content/${item.contentFolderId}/media/${item.id}/details`);
    },
    [link],
  );

  return useMemo(() => {
    const columns: GridColDef<ContentOrFolder>[] = [
      {
        field: 'thumbnail',
        ...hiddenHeader(t('common.labels.thumbnail')),
        width: 84,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.__typename === 'ContentFolder') {
            return (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                <FolderIcon />
              </Box>
            );
          }
          return (
            <Box display="flex" alignItems="center" justifyContent="center" width="100%">
              <ItemThumbnail
                thumbnailUri={row.thumbnailUri}
                fallbackIcon={<Apps sx={{ color: 'text.secondary', fontSize: 20 }} />}
              />
            </Box>
          );
        },
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
      },
      {
        field: 'kind',
        headerName: t('common.labels.type'),
        renderCell: ({ row }) => {
          if (row.__typename === 'ContentFolder') {
            return t('common.record_types.folder').toUpperCase();
          }

          let type: string;
          if (row.__typename === 'ContentItem' && row.design?.id) {
            type = t('common.record_types.design').toUpperCase();
          } else if (row.__typename === 'MediaItem') {
            type = mediaExtension(row).toUpperCase();
          } else {
            type = t('common.record_types.app').toUpperCase();
          }

          return (
            <>
              {type}
              {row.overlay && `, ${t('common.record_types.overlay').toUpperCase()}`}
            </>
          );
        },
        sortable: false,
        minWidth: 175,
      },
      {
        field: 'itemCount',
        headerName: t('pages.content.list.columns.items'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          if (row.__typename === 'ContentFolder') {
            return <Chip color="primary" label={row.itemCount || 0} />;
          }
          return '--';
        },
        minWidth: 150,
      },
      {
        field: 'originalMetadata',
        headerName: t('common.labels.size'),
        renderCell: ({ row: item }) => {
          if (item.__typename === 'ContentFolder' || item.__typename === 'ContentItem') return '--';
          return formatBytes(mediaSize(item));
        },
        sortable: false,
        minWidth: 100,
      },
    ];

    search
      ? columns.push({
          field: 'location',
          headerName: t('pages.content.list.columns.location'),
          minWidth: 250,
          renderCell: ({ row }) => {
            if (row.__typename === 'ContentFolder') return;
            const contentFolderId =
              // there will be a content folder id here
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              row.__typename === 'ContentItem' ? row.nullableContentFolderId! : row.contentFolderId;
            return <ContentLocation contentFolderId={contentFolderId} />;
          },
          sortable: false,
        })
      : columns.push({
          field: 'createdAt',
          headerName: t('common.labels.created'),
          minWidth: 250,
          sortable: false,
          renderCell: ({ row }) => {
            return formatDate(row.createdAt);
          },
        });

    columns.push({
      field: 'actions',
      type: 'actions',
      minWidth: isSmallAndUp ? 150 : 0,
      getActions: ({ row: item }) => {
        const actions = [];

        if (item.__typename === 'ContentFolder') {
          actions.push(<ActionButton sx={{ visibility: 'hidden' }} title="" Icon={Info} />);
        }

        if (item.__typename !== 'ContentFolder') {
          actions.push(
            <ActionButtonLink title={t('common.labels.details')} Icon={Info} to={getLink(item)} />,
          );
        }

        actions.push(
          <ContentMenu
            key={`more-action-${item.id}`}
            item={item}
            Icon={MoreHoriz}
            buttonType="action"
          />,
        );

        return actions;
      },
    });

    return columns;
  }, [getLink, isSmallAndUp, search, t]);
};
